import React, { useEffect, useRef } from 'react';
import mapboxgl from 'mapbox-gl';
mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN;

// Color palette for sub-site polygons
const SUB_SITE_COLORS = [
    '#e74c3c', '#2ecc71', '#3498db', '#f39c12',
    '#9b59b6', '#1abc9c', '#e67e22', '#34495e',
    '#16a085', '#c0392b', '#2980b9', '#8e44ad'
];

function MapboxMap({ geojson, subSites = [], isFormView = false, mapId = 'form-map', isCollapsed }) {
    const mapRef = useRef(null);

    useEffect(() => {
        const map = new mapboxgl.Map({
            container: mapId,
            style: 'mapbox://styles/mapbox/satellite-streets-v11',
            center: [78.9629, 20.5937],
            zoom: 4,
        });

        mapRef.current = map;

        if (geojson && map) {
            let geometry = typeof geojson === 'string' ? JSON.parse(geojson) : geojson;
            
            // Convert to FeatureCollection if needed
            let featureCollection;
            if (geometry.type === 'FeatureCollection') {
                featureCollection = geometry;
            } else if (geometry.type === 'Feature') {
                featureCollection = {
                    type: 'FeatureCollection',
                    features: [geometry]
                };
            } else {
                // It's a raw geometry object (Polygon, MultiPolygon, etc.)
                featureCollection = {
                    type: 'FeatureCollection',
                    features: [{
                        type: 'Feature',
                        geometry: geometry,
                        properties: {}
                    }]
                };
            }

            map.on('load', () => {
                map.addSource('site', {
                    type: 'geojson',
                    data: featureCollection,
                });

                map.addLayer({
                    id: 'site-fill',
                    type: 'fill',
                    source: 'site',
                    paint: {
                        'fill-color': '#4880ff',
                        'fill-opacity': subSites.length > 0 ? 0.15 : 0.4,
                    },
                });

                map.addLayer({
                    id: 'site-outline',
                    type: 'line',
                    source: 'site',
                    paint: {
                        'line-color': '#4880ff',
                        'line-width': subSites.length > 0 ? 2 : 3,
                        ...(subSites.length > 0 ? { 'line-dasharray': [4, 2] } : {}),
                    },
                });

                // Render sub-site polygons with distinct colors
                if (subSites.length > 0) {
                    const subSiteFeatures = subSites.map((ss, i) => {
                        let ssGeometry = ss.geometry;
                        if (typeof ssGeometry === 'string') {
                            try { ssGeometry = JSON.parse(ssGeometry); } catch (e) { return null; }
                        }
                        if (!ssGeometry) return null;
                        return {
                            type: 'Feature',
                            geometry: ssGeometry,
                            properties: {
                                name: ss.name || ss.sub_site_id,
                                color: SUB_SITE_COLORS[i % SUB_SITE_COLORS.length],
                            }
                        };
                    }).filter(Boolean);

                    if (subSiteFeatures.length > 0) {
                        const subSiteCollection = { type: 'FeatureCollection', features: subSiteFeatures };

                        map.addSource('sub-sites', { type: 'geojson', data: subSiteCollection });

                        map.addLayer({
                            id: 'sub-site-fill',
                            type: 'fill',
                            source: 'sub-sites',
                            paint: {
                                'fill-color': ['get', 'color'],
                                'fill-opacity': 0.3,
                            },
                        });

                        map.addLayer({
                            id: 'sub-site-outline',
                            type: 'line',
                            source: 'sub-sites',
                            paint: {
                                'line-color': ['get', 'color'],
                                'line-width': 2.5,
                            },
                        });

                        map.addLayer({
                            id: 'sub-site-labels',
                            type: 'symbol',
                            source: 'sub-sites',
                            layout: {
                                'text-field': ['get', 'name'],
                                'text-size': 12,
                                'text-anchor': 'center',
                                'text-allow-overlap': true,
                            },
                            paint: {
                                'text-color': '#ffffff',
                                'text-halo-color': '#000000',
                                'text-halo-width': 1.5,
                            },
                        });
                    }
                }

                try {
                    const bounds = new mapboxgl.LngLatBounds();
                    const feature = featureCollection.features[0];
                    const coords = feature.geometry.coordinates;
                    const type = feature.geometry.type;

                    if (type === 'Polygon') {
                        coords[0].forEach(coord => {
                            bounds.extend(coord);
                        });
                    } else if (type === 'MultiPolygon') {
                        coords.forEach(polygon => {
                            polygon[0].forEach(coord => {
                                bounds.extend(coord);
                            });
                        });
                    }

                    if (!bounds.isEmpty()) {
                        map.fitBounds(bounds, {
                            padding: 50,
                            maxZoom: 16,
                            duration: 1000
                        });
                    }
                } catch (err) {
                    console.error("Error calculating bounds:", err);
                }
            });
        }

        return () => map.remove();
    }, [geojson, subSites, mapId]);

    // 🔥 Best Practice: Listen to sidebar's transition and resize map properly
    useEffect(() => {
        const sidebar = document.querySelector('.side-nav');

        const handleTransitionEnd = () => {
            if (mapRef.current) {
                mapRef.current.resize();
            }
        };

        if (sidebar) {
            sidebar.addEventListener('transitionend', handleTransitionEnd);
        }

        return () => {
            if (sidebar) {
                sidebar.removeEventListener('transitionend', handleTransitionEnd);
            }
        };
    }, []);

    return (
        <div
            id={mapId}
            style={{
                height: isFormView ? '100%' : '300px',
                width: '100%',
                margin: 0,
                padding: 0,
                borderRadius: isFormView ? '0' : '8px',
                overflow: 'hidden',
            }}
        />
    );
}

export default MapboxMap;

